// The committed-budget bound on the Funnel's cohort, as the URL and the query
// key carry it. USD, either side open, both open = no bound.
//
// The CONTROL opens on 0 to 100,000 so a reader sees a sensible range to edit
// rather than two empty boxes, but those defaults are not a filter: nothing is
// sent until a bound differs from them, so an untouched control leaves the page
// on its cheap precomputed lane and the URL clean. The server applies the bound
// to `budget_mid_usd`, the money funnel's own figure, and KEEPS a project with no
// readable budget (it is neither over nor under a bound) — see
// backend/recruiter/repo/admin_insights.py `_budget_scope`.

export const DEFAULT_BUDGET_MIN = 0;
export const DEFAULT_BUDGET_MAX = 100_000;

export type BudgetRange = {
  /** Inclusive USD floor, or undefined for open. */
  min?: number;
  /** Inclusive USD ceiling, or undefined for open. */
  max?: number;
};

export const EMPTY_BUDGET_RANGE: BudgetRange = {};

export function isBudgetActive(budget: BudgetRange): boolean {
  return budget.min !== undefined || budget.max !== undefined;
}

/** One `?budget_min=` / `?budget_max=` value off the URL: a finite, non-negative
 *  number, or undefined for anything else. A malformed value is dropped rather
 *  than sent, since the server would 422 the whole request over it. */
export function parseBudgetParam(value: unknown): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) && value >= 0 ? value : undefined;
  if (typeof value !== "string" || value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

/** The bound as one stable key fragment: `-` for an open side, and the numbers
 *  spelled the way the URL spells them, so `500` and `500.0` are one key. */
export function budgetKey(budget: BudgetRange): string {
  if (!isBudgetActive(budget)) return "-";
  return `${budget.min ?? "-"}:${budget.max ?? "-"}`;
}

/** What the two boxes show: the bound in force, else the default. */
export function budgetBounds(budget: BudgetRange): { min: number; max: number } {
  return { min: budget.min ?? DEFAULT_BUDGET_MIN, max: budget.max ?? DEFAULT_BUDGET_MAX };
}

/** Turn what the reader typed into the bound to apply: a value equal to its
 *  default is NOT a bound (it comes off the URL), an inverted or non-numeric
 *  pair is refused (undefined) so the caller can snap the boxes back. */
export function budgetFromInputs(minText: string, maxText: string): BudgetRange | undefined {
  const min = parseBudgetParam(minText);
  const max = parseBudgetParam(maxText);
  if (min === undefined || max === undefined || min > max) return undefined;
  return {
    min: min === DEFAULT_BUDGET_MIN ? undefined : min,
    max: max === DEFAULT_BUDGET_MAX ? undefined : max,
  };
}
